import React, { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import useSWR from 'swr';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { ReportView } from '../../../components/ui';
import { fetcher } from '../../../services/fetcher';
import {
  ArrangementsReport,
  type ArrangementsReportData,
} from './ArrangementsReport';
import { useManagementCompanyLogoBlobUrl } from './useManagementCompanyLogo';

const ArrangementsReportPage: React.FC = () => {
  const { colors } = useTheme();
  const [params] = useSearchParams();
  const vesselId = params.get('vesselId')?.trim() || '';

  const key = vesselId ? `/wrh/arrangements?vesselId=${encodeURIComponent(vesselId)}` : null;
  const { data, error, isLoading } = useSWR<ArrangementsReportData>(key, fetcher, {
    revalidateOnFocus: false,
  });
  const { logoBlobUrl: companyLogoSrc } = useManagementCompanyLogoBlobUrl(
    data?.managementCompany?.id,
    data?.managementCompany?.logoStorageKey,
  );

  const backHref = useMemo(
    () => (vesselId ? `/wrh/arrangements?vesselId=${encodeURIComponent(vesselId)}` : '/wrh/arrangements'),
    [vesselId],
  );

  if (!vesselId) {
    return (
      <div className={cn('flex min-h-[40vh] flex-col items-center justify-center gap-3 px-4', colors.background)}>
        <p className={cn('text-sm', colors.textMuted)}>Select a vessel on the arrangements page first.</p>
        <Link to="/wrh/arrangements" className={cn('text-sm font-medium', colors.primaryFg, 'hover:underline')}>
          ← Back to arrangements
        </Link>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={cn('flex min-h-[40vh] items-center justify-center text-sm', colors.textMuted)}>
        Loading…
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className={cn('flex min-h-[40vh] flex-col items-center justify-center gap-3 px-4', colors.background)}>
        <p className={cn('text-sm', colors.textMuted)}>Could not load working arrangements.</p>
        <Link to={backHref} className={cn('text-sm font-medium', colors.primaryFg, 'hover:underline')}>
          ← Back to arrangements
        </Link>
      </div>
    );
  }

  const empty = !data.crew.length;

  return (
    <ReportView
      backTo={backHref}
      backLabel="← Back to arrangements"
      title="Table of shipboard working arrangements"
      subtitle={
        <>
          {data.vesselName}
          {data.vesselAbrv ? ` (${data.vesselAbrv})` : ''} · A4 landscape
        </>
      }
      printPageSize="A4 landscape"
      printMargin="0"
      empty={empty}
      emptyMessage="No onboard crew found for this vessel. Open arrangements and ensure crew are signed on."
    >
      <ArrangementsReport data={data} companyLogoSrc={companyLogoSrc} />
    </ReportView>
  );
};

export default ArrangementsReportPage;
